import { headFor } from './piece-models.js';
import { foldPackageName } from './prefix.js';
/**
 * The status of a copy against one build, `latest` — which {@link evaluateOnChannel} picks so that
 * for a copy on the Board it is the head of its own sibling. (`InstallState.evaluate` in Kotlin.)
 */
export function evaluateBuild(latest, facts) {
    const boardReported = facts.boardReportedVersion ?? null;
    const ledger = facts.ledgerVersion ?? null;
    const pin = facts.pin ?? null;
    if (!latest) {
        return { status: 'no_build', installedVersionCode: boardReported ?? ledger, latestVersionCode: null, pin: null };
    }
    if (!facts.installedOnBoard) {
        // The ledger is stale the moment someone uninstalls outside BoardFlight, so Board's own answer
        // wins on the question of presence.
        return { status: 'not_installed', installedVersionCode: null, latestVersionCode: latest.versionCode, pin: null };
    }
    const installed = boardReported ?? ledger;
    if (installed === null) {
        return { status: 'installed', installedVersionCode: null, latestVersionCode: latest.versionCode, pin };
    }
    // A pin only holds while the pinned build is the one actually installed. If the copy moved on
    // some other way, the pin is stale and honouring it would hide a real update.
    const livePin = pin && pin.versionCode === installed ? pin : null;
    const status = livePin && installed < latest.versionCode
        ? 'pinned'
        : installed < latest.versionCode
            ? 'update_available'
            : installed > latest.versionCode
                ? 'newer_than_latest'
                : 'up_to_date';
    return { status, installedVersionCode: installed, latestVersionCode: latest.versionCode, pin: livePin };
}
/**
 * {@link evaluateBuild}, against the build of `channel` this Board's copy is actually measured by.
 * (`InstallState.evaluateOn` in Kotlin.)
 *
 * Not on the Board, that is the channel's head: there is nothing to compare, and the question is
 * only whether anything is published. On the Board, it is the head of the **installed sibling**
 * ({@link headFor}), so a build for other pieces is never an update and a same-code build for other
 * pieces never makes the wrong copy look current.
 *
 * A copy whose sibling this channel no longer has is `up_to_date` with no latest version: nothing
 * newer exists for what is installed, so nothing is offered, and the game keeps its Play button.
 * That is a narrower claim than "this is the channel's newest build", and the only one available.
 */
export function evaluateOnChannel(channel, facts) {
    const published = channel?.latestBuild ?? null;
    if (!channel || !published || !facts.installedOnBoard)
        return evaluateBuild(published, facts);
    const head = headFor(channel, facts.lastInstall);
    if (!head) {
        return {
            status: 'up_to_date',
            installedVersionCode: facts.boardReportedVersion ?? facts.ledgerVersion ?? null,
            latestVersionCode: null,
            pin: null,
        };
    }
    return evaluateBuild(head, { ...facts, installedOnBoard: true });
}
/** Whether pressing the button would download and install something. */
export function canInstall(info) {
    return info.status === 'not_installed' || info.status === 'update_available' || info.status === 'installed';
}
/** Whether the status says the game is on the Board. */
export function isInstalled(info) {
    return info.status !== 'not_installed' && info.status !== 'no_build';
}
/** The chip's words. */
export function statusLabel(info) {
    switch (info.status) {
        case 'no_build':
            return 'No build yet';
        case 'not_installed':
            return 'Not installed';
        case 'installed':
            return 'Installed';
        case 'up_to_date':
            return 'Up to date';
        case 'update_available':
            return `Update available (${info.installedVersionCode} → ${info.latestVersionCode})`;
        case 'newer_than_latest':
            return 'Newer than latest';
        case 'pinned':
            return `Pinned to ${info.pin?.versionName ?? info.installedVersionCode} · newer available`;
    }
}
/**
 * The verb that goes with a status.
 *
 * "Play" rather than "Open" for anything already on the Board: what a team hands a tester a Board
 * to try is almost always a game, and on a row in a list the verb is doing most of the work of
 * saying what the button is for.
 */
export function statusAction(info) {
    switch (info.status) {
        case 'no_build':
            return 'Unavailable';
        case 'not_installed':
            return 'Install';
        case 'update_available':
            return 'Update';
        case 'installed':
            return 'Reinstall';
        default:
            return 'Play';
    }
}
/** The ledger as an {@link InstallRecord}, or null when BoardFlight never installed this game here. */
export function installRecordOf(state) {
    if (!state || state.buildId === null)
        return null;
    return {
        buildId: state.buildId,
        versionName: state.buildVersionName,
        versionCode: state.buildVersionCode,
        atMillis: state.installedAt,
        pieceModel: state.pieceModel,
    };
}
/**
 * How long an install that just finished counts as "Board has not caught up yet".
 *
 * Board's `GET /v1/apps` is authoritative on presence and it is not instantaneous: the install call
 * returns when the package installer has accepted the build, and the app appears in the list a
 * moment later. In that window BoardFlight knows the game is on the Board and Board does not say
 * so — which is not an error state, it is the last second of an install.
 *
 * Long enough for a Board that is busy finishing a large install, short enough that a game which
 * genuinely never appeared goes back to offering Install rather than sitting on a dead button.
 */
export const SETTLE_WINDOW_MILLIS = 20_000;
/**
 * Installed by BoardFlight a moment ago, and not in Board's app list yet.
 *
 * The one place the ledger is allowed to disagree with Board about presence, and it is bounded in
 * both directions: only for an install BoardFlight just finished, and only for
 * {@link SETTLE_WINDOW_MILLIS}. What it buys is the seconds after an install, in which the library
 * used to offer *Install* for a game that had just been installed — a button that reads as "that
 * did not work" and which, pressed, downloads a few hundred megabytes again.
 */
export function isSettling(lastInstall, installedOnBoard, now) {
    if (installedOnBoard || !lastInstall || lastInstall.atMillis === null)
        return false;
    const since = now - lastInstall.atMillis;
    return since >= 0 && since < SETTLE_WINDOW_MILLIS;
}
/* ------------------------------------------------------------- remove first, or not */
/**
 * Whether installing `target` over what is there now needs the game removed first.
 *
 * Board rejects an in-place downgrade outright — confirmed on hardware,
 * `INSTALL_FAILED_VERSION_DOWNGRADE` — so the only route to an older build destroys the game's
 * saved data. When the installed version is unknown this is false: attempting the install and
 * reacting to Board's refusal is better than destroying data on a guess.
 */
export function needsRemoveFirst(installedVersionCode, target) {
    return installedVersionCode !== null && installedVersionCode !== undefined && target.versionCode < installedVersionCode;
}
/**
 * The build following `channel` would offer, when it is older than what is installed — or null
 * when following it is no step down (`docs/game-events-plan.md` §6.4).
 *
 * Builds for different channels carry different version codes, so a game shipped to `paid` and
 * `free` has one of them lower. Moving down is a build Board refuses to install over the newer
 * one, which leaves only remove-and-reinstall — and that deletes the game's saves. The picker says
 * so before the choice, not at the install after it.
 *
 * The build compared is the one following would actually install: the head of the installed sibling
 * on that channel, not its head across every model.
 */
export function channelDowngrade(installedVersionCode, channel, lastInstall) {
    const head = headFor(channel, lastInstall);
    return head && needsRemoveFirst(installedVersionCode, head) ? head : null;
}
const DOWNGRADE_MARKER = 'INSTALL_FAILED_VERSION_DOWNGRADE';
/**
 * Board's own wording for "that build is older than the one installed".
 *
 * The backstop for when the installed version was unknown up front — a web app reports none, and
 * nothing is known before the first install. Reading Board's error is what turns a dead end into
 * an offer to remove and retry.
 */
export function isDowngradeRefusal(boardMessage) {
    return !!boardMessage && boardMessage.toUpperCase().includes(DOWNGRADE_MARKER);
}
/**
 * The stand-in for "the developer's own signature", which a build record reports as null.
 *
 * Not a certificate hash and never compared to one — only to itself. It exists because "signed by
 * the developer" and "no idea" are different answers, and only the first can be compared.
 */
export const DEVELOPER_SIGNED = 'developer';
/**
 * The certificate of the copy on a Board, as far as BoardFlight knows: a hash,
 * {@link DEVELOPER_SIGNED}, or null for "no idea".
 *
 * Null whenever BoardFlight did not install the copy that is there — never installed it, or the
 * last sync did not list it. This is a record of what BoardFlight installed, not a reading of the
 * device, and a game that arrived some other way gets no warning rather than a guess.
 */
export function installedCertificateOf(state) {
    if (!state || !state.installed || state.buildId === null)
        return null;
    return state.signingCertSha256 ?? DEVELOPER_SIGNED;
}
/**
 * Has the signing identity changed since BoardFlight installed this game? The signing gate.
 *
 * Turning the in-game layer on or off changes which key a build is signed with, and Android
 * refuses an update whose certificate differs from the installed app's — *after* the download,
 * with "App not installed" and no explanation. This comparison is what turns that into an offer
 * the person can act on, before a byte moves.
 *
 * Null on the left is "no idea", which must not become a warning: guessing wrong in that direction
 * tells somebody to delete their saved data for no reason.
 *
 * @param installedCertificate {@link installedCertificateOf} the copy on the Board.
 * @param incoming `signingCertSha256` of the build about to be installed; null is the developer's.
 */
export function signatureChanged(installedCertificate, incoming) {
    if (installedCertificate === null || installedCertificate === undefined)
        return false;
    return installedCertificate !== (incoming ?? DEVELOPER_SIGNED);
}
/** An entry with no `kind` is an APK: that was the only kind before web apps existed. */
export function isApkEntry(app) {
    return app.kind === null || app.kind === undefined || app.kind === 'apk';
}
/**
 * The package id an entry *is*, as the library names it — not how Board addresses it.
 *
 * The two differ for a web app, and only for a web app. Board runs every web app inside one browser
 * host, so a web app's `packageName` is that host's package (`co.harrishill.boardbrowser`) —
 * identical for every web app on the device — while the id the bundle declared in
 * `harness-config.json`, and the one BoardFlight authorized the build for, is `packageId`.
 *
 * Null when firmware reports neither. A caller must not fall back to the addressing id, which for
 * a web app is a device-minted UUID no library record can match.
 */
export function libraryPackageId(app) {
    if (isApkEntry(app))
        return app.packageName ?? app.packageId ?? null;
    return app.packageId ?? null;
}
/**
 * Board's installed apps, keyed by the package id the library knows them by — **folded**.
 *
 * Entries that name no package id are dropped rather than keyed by something else: a web app whose
 * `packageId` is missing cannot be recognised, and inventing a key for it would collapse every such
 * entry onto one game.
 *
 * Folded with {@link foldPackageName}, because that is what the server means by "the same package":
 * a build whose manifest says `com.boardaux.JuniperGus` publishes legitimately under an app record
 * saying `com.boardaux.junipergus`. A map keyed by the raw string made a game sitting on the Board
 * read as not installed on every screen — invisible to every test, because every fixture spells
 * both sides the same. The first entry for an id wins.
 */
export function indexInstalledApps(apps) {
    const index = new Map();
    for (const app of apps) {
        const id = libraryPackageId(app);
        if (id === null)
            continue;
        const key = foldPackageName(id);
        if (!index.has(key))
            index.set(key, app);
    }
    return index;
}
/**
 * Look one up the way it was filed. **The only correct way to read that map** — a bare
 * `index.get(packageName)` is wrong for exactly the ids nobody tests with.
 */
export function findInstalledApp(index, packageName) {
    if (packageName === null || packageName === undefined)
        return null;
    return index.get(foldPackageName(packageName)) ?? null;
}
/* ----------------------------------------------------------------- which channel */
/**
 * What a player sees before they have chosen anything: **a channel that has a build**, and only
 * then the developer's default.
 *
 * `isDefault` is the developer's word about where builds are *meant* to come from, and it is set
 * on a channel long before anything is published to it — `internal` is the default on a team whose
 * only build so far went to `playground`. Following the default there opens a game onto "No build
 * yet" while a build sits one channel over. So an empty default loses to a channel that can
 * actually be installed; with nothing published anywhere, the developer's default stands.
 */
export function primaryChannel(app) {
    const channels = app.channels;
    return (channels.find((channel) => channel.isDefault && channel.latestBuild !== null) ??
        channels.find((channel) => channel.latestBuild !== null) ??
        channels.find((channel) => channel.isDefault) ??
        channels[0] ??
        null);
}
/**
 * The channel a game is followed on: the one this Board chose, else {@link primaryChannel}.
 *
 * `slug` is a stored preference, so it can name a channel the account is no longer entitled to.
 * Falling back rather than showing nothing matters: a game that emptied itself because of a stale
 * preference would read as access having been taken away. A choice that still exists is honoured
 * even when that channel has published nothing yet — that is how a tester waits for a build.
 */
export function channelFollowed(app, slug) {
    return app.channels.find((channel) => channel.slug === slug) ?? primaryChannel(app);
}
/**
 * A library entry and a Board's row for it, to one of the seven states.
 *
 * `state` null is a Board no desktop has synced this game for, and reads exactly as "not
 * installed": the library still says whether there is anything to install.
 */
export function evaluate(app, state, options = {}) {
    // The whole entry hangs off this one line: the status, the version on offer, what Install
    // installs. Following another channel is this and nothing else.
    const channel = channelFollowed(app, options.channelSlug ?? state?.channelSlug ?? null);
    const onBoard = state?.installed === true;
    const record = installRecordOf(state);
    // Dropped the moment the game is not on the Board any more: a record of an install that has
    // since been removed is a claim about the device that stopped being true. Read before the
    // status, because it says which sibling is installed and so which build the status is measured by.
    const lastInstall = onBoard ? record : null;
    return {
        channel,
        status: evaluateOnChannel(channel, {
            installedOnBoard: onBoard,
            lastInstall,
            boardReportedVersion: state?.versionCode ?? null,
            ledgerVersion: record?.versionCode ?? null,
            pin: state?.pin ?? null,
        }),
        onBoard,
        lastInstall,
        head: channel ? (onBoard ? headFor(channel, lastInstall) : channel.latestBuild) : null,
        settling: options.now === undefined ? false : isSettling(record, onBoard, options.now),
    };
}
/**
 * {@link evaluate} against another of the game's channels — or null when the account cannot see
 * one by that name. For a press made on a page about a channel other than the one the Board
 * follows: a member following `internal` at 7 sees *Update (7 → 12)* on the `public` page, and
 * judging that press against the followed channel found 7 already there and did nothing at all.
 */
export function onChannel(app, state, slug, options = {}) {
    if (!app.channels.some((channel) => channel.slug === slug))
        return null;
    return evaluate(app, state, { ...options, channelSlug: slug });
}
/** {@link evaluate}, in the shape `/v1/me/library?board=` hangs on each game. */
export function boardGameOf(app, state) {
    const game = evaluate(app, state);
    return {
        state: state ?? null,
        channelSlug: game.channel?.slug ?? null,
        status: game.status,
        headBuildId: game.head?.id ?? null,
    };
}
/**
 * The one obvious thing to do with a game, and what to call it.
 *
 * Asked by the library row's button and the game page's first action, so it is answered once, as a
 * pure function of the entry. A row that offers Install for something already installed, or Play
 * for something that is not, is worse than a row with no button on it at all.
 *
 * @param pieces what `resolveInstall` said about this channel, or null where the caller has no
 *   inventory to resolve against — a screen that has not been given one asks nothing and is
 *   answered exactly as it was before pieces existed.
 */
export function primaryAction(game, pieces) {
    const hasBuild = !!game.channel?.latestBuild;
    // `onBoard` and not just the status: launching needs Board to list the game, and a status can
    // say "installed" on the ledger's word alone.
    const installed = isInstalled(game.status) && game.onBoard;
    // Before every other branch, and before `canInstall` in particular: the status this overrides is
    // `not_installed`, which is what Board says and is about to stop being true.
    if (game.settling)
        return { kind: 'finishing', label: 'Finishing…' };
    // Before Install and after Finishing, and **only when nothing is installed**. BoardFlight cannot
    // reach onto a Board and does not pretend to: a game already there keeps its Play button and gets
    // the notice beside it. What is refused is putting new bytes on a Board that cannot run them.
    if (pieces?.kind === 'missing' && !installed)
        return { kind: 'blocked', label: 'Pieces needed' };
    if (canInstall(game.status) && hasBuild)
        return { kind: 'install', label: statusAction(game.status) };
    // "Play", not the status's own word. `installed` says "Reinstall" — right when there is a build
    // to reinstall from, and a lie once this branch is reached, because reaching it means there is not.
    if (installed)
        return { kind: 'open', label: 'Play' };
    // Deliberately "Install" rather than the status's own "Unavailable": this is a button about to
    // be drawn disabled, and the chip beside it already says why.
    return { kind: 'unavailable', label: 'Install' };
}
//# sourceMappingURL=install-state.js.map